/**
 * Ticket-group count for the Events tab bar (2026-10-02, Nick). Reads the same
 * v2 op the Tickets screen lists from — GET /v1/admin/ticket-group-library — so
 * the tab and the screen agree. Undefined until the read resolves (or on error):
 * the tab then shows no count rather than a wrong one.
 * Data: bound (listTicketGroupLibrary via tv2Call).
 *
 * The count belongs to ONE tenant (CC, 2026-10-03). A workspace switch clears it and
 * bumps `gen`; the confirmed change re-reads it while the tab is mounted. A read that
 * started under an earlier `gen` never fills the cache or notifies, so the previous
 * tenant's count cannot show, or overwrite the new tenant's, even when it lands late.
 * The tenant hooks register on first use, so importing this module has no side effects.
 */
import { useEffect, useState } from "react";
import { tv2Call } from "./tickets-v2-api";
import { onTenantChange, onTenantSwitching } from "../../../shell/access";

let cached: number | undefined;
let inflight: Promise<void> | null = null;
let gen = 0;
/** Bumped by every count that lands, so an older read never overwrites a newer count (CC, 2026-10-04). */
let seq = 0;
let hooked = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

function hookTenant() {
  if (hooked) return;
  hooked = true;
  try {
    onTenantSwitching(() => { gen++; cached = undefined; inflight = null; emit(); });
    onTenantChange(() => { if (subs.size) void refreshTicketGroupCount(); });
  } catch { /* no tenant lifecycle (an isolated test): one tenant for the module's life */ }
}

/** The Tickets screen calls this after its own list read, so the tab updates without a second request. */
export function noteTicketGroupCount(n: number) { seq++; cached = n; emit(); }

export function refreshTicketGroupCount(): Promise<void> {
  hookTenant();
  if (inflight) return inflight;
  const g = gen;
  const mine = ++seq;
  const p: Promise<void> = tv2Call<{ groups?: unknown[] }>("GET", "/v1/admin/ticket-group-library")
    .then((r) => { if (g === gen && mine === seq) cached = Array.isArray(r?.groups) ? r.groups.length : undefined; })
    .catch(() => { if (g === gen && mine === seq) cached = undefined; })
    .finally(() => { if (g !== gen) return; if (inflight === p) inflight = null; emit(); });
  inflight = p;
  return p;
}

/** `enabled: false` (a role without products.read) skips the read and shows no count. */
export function useTicketGroupCount(enabled = true): number | undefined {
  const [, bump] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    hookTenant();
    const f = () => bump((n) => n + 1);
    subs.add(f);
    if (cached === undefined && !inflight) void refreshTicketGroupCount();
    return () => { subs.delete(f); };
  }, [enabled]);
  return enabled ? cached : undefined;
}
